import { html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhWarning";
import { BodyScrollManager, DataQueryController, WowFormController } from "ui-common";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/modal/wow-modal.ts";
import "ui-components/social-media-item/social-media-item";
import "ui-components/sticky-button-container/wow-sticky-button-container.ts";
import "ui-components/wizard/wow-wizard";
import "ui-components/wow-logo";
import { minimalViableCreator } from "helpers/minimalViableProfile";

import { styles } from "./onboard-creator.css";

import "../../components/avatar-editor/avatar-editor";
import "../../components/custom-color-picker/custom-color-picker";
import "../../components/profile-color-selector/profile-color-selector";
import "../../components/sign-up-create/wow-sign-up-create";
import "../../components/social-links-section/social-links-section";
import "../../components/user-background-image/user-background-image";
import "../../components/wow-logo-full/wow-logo-full";
import "../../components/wow-logo-large/wow-logo-large";
import { profileDataQueryOptions } from "../../queries/profile-data";
import { navigateTo } from "../../router";
import { saveProfileMutation } from "../../services/profile-mutations";
import { t, translate } from "../../translations/i18n";
import { getUsernamePrefix, platformConfigs } from "ui-common/socialMediaConfig";

@customElement("onboard-creator-page")
export class OnboardCreatorPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  static override styles = [styles];

  @state()
  private currentStep = 0;

  @state()
  private userEmail = "";

  // Onboarding form state
  @state()
  private handleError = "";

  @state()
  private isHandleAvailable = false;

  @state()
  private currentSocialLinks: string[] = [];

  @state()
  private isAddSocialModalOpen = false;

  @state()
  private newlyAddedLinkIndex = -1;

  @state()
  private hasSocialLinks = false;

  @state()
  private socialLinksError = t("onboarding.requireSocialLink") as string;

  // Step-2 state (previously step 3)
  @state()
  private profileImageFileId?: string;

  @state()
  private profileCoverFileId?: string;

  @state()
  private selectedThemeId?: string;

  @state()
  private selectedThemeColors?: { primary: string; secondary: string };

  @state()
  private customColors?: { primary: string; secondary: string };

  @state()
  private isCustomColorModalOpen = false;

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  private onboardFormController: WowFormController<{ handle: string }> = new WowFormController(
    this,
    { handle: "" },
    {
      validateOnBlur: true,
      validators: {
        handle: [this.validateHandle.bind(this)],
      },
    },
    async (event) => {
      if (!event?.data || !event.isValid || !this.isHandleAvailable) return;
      if (this.currentStep === 0) return;

      if (this.currentStep === 1) {
        // Step 2: Handle and social links validation
        const socialLinksComponent = this.renderRoot.querySelector("social-links-section");
        if (!socialLinksComponent) return;

        const socialLinksValid = socialLinksComponent.validateAllLinks();
        const hasSocialLinks = this.currentSocialLinks.length > 0;

        if (!hasSocialLinks) {
          this.socialLinksError = translate("onboarding.requireSocialLink");
          return;
        }

        if (!socialLinksValid) {
          this.socialLinksError = "";
          return;
        }

        this.socialLinksError = "";

        // First, convert the fan account to a creator account
        try {
          await rpc("createCreator", {
            handle: event.data.handle,
          });

          // Move to step 2 (previously step 3)
          const wizard = this.renderRoot.querySelector("wow-wizard");
          wizard?.next();
        } catch (error) {
          console.error("Error creating influencer:", error);
          if (typeof error === "string" && error.includes("already a creator")) {
            // User is already a creator, just proceed to next step
            const wizard = this.renderRoot.querySelector("wow-wizard");
            wizard?.next();
          } else {
            this.handleError = translate("profile.notifications.saveError");
          }
        }
        return;
      }

      if (this.currentStep === 2) {
        // Step 2: Final submission with all data (previously step 3)
        if (!this.isStep2Valid()) {
          return;
        }

        try {
          await saveProfileMutation.mutate({
            socialLinks: this.currentSocialLinks,
            profileImageFileId: this.profileImageFileId,
            profileCoverFileId: this.profileCoverFileId,
            selectedThemeId: this.selectedThemeId,
            customColors: this.customColors,
          });

          const handle = this.onboardFormController.getData()?.handle;
          if (handle) {
            location.href = "/" + handle;
          } else {
            navigateTo("profile");
          }
        } catch (error) {
          console.error("Error saving profile:", error);
          this.handleError = translate("profile.notifications.saveError");
        }
      }
    },
    "#onboard-form",
  );

  override disconnectedCallback() {
    super.disconnectedCallback();

    // Enable body scroll
    BodyScrollManager.enableScroll();
    document.body.style.left = "";
    document.body.style.right = "";
  }

  override connectedCallback() {
    super.connectedCallback();

    // Initialize form controller with current user's handle
    const currentUser = this.authState.currentUser.get();
    if (currentUser?.handle) {
      this.onboardFormController.reset({ handle: currentUser.handle });
      this.isHandleAvailable = true;
    }

    // Prevent body scroll
    BodyScrollManager.disableScroll();
    document.body.style.left = `0`;
    document.body.style.right = `0`;

    // Check URL parameters for step
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get("step");

    if (stepParam) {
      const step = parseInt(stepParam, 10);

      // Step 1 is only allowed for authenticated users
      if (step === 1) {
        if (this.authState.isAuthenticated.get()) {
          this.currentStep = 1;
        } else {
          // Unauthenticated users trying to access step 1 get redirected to step 0
          this.currentStep = 0;
          // Optionally update URL to reflect the actual step
          const url = new URL(window.location.href);
          url.searchParams.delete("step");
          window.history.replaceState(null, "", url.toString());
        }
        return;
      }

      // Step 2 is only allowed for authenticated users (previously step 3)
      if (step === 2) {
        if (this.authState.isAuthenticated.get()) {
          if (
            this.profileDataQuery.result?.isSuccess &&
            this.profileDataQuery.result?.data &&
            minimalViableCreator(this.profileDataQuery.result?.data)
          ) {
            navigateTo("profile");
            return;
          }
          this.currentStep = 2;
        } else {
          // Unauthenticated users trying to access step 2 get redirected to step 0
          this.currentStep = 0;
          // Optionally update URL to reflect the actual step
          const url = new URL(window.location.href);
          url.searchParams.delete("step");
          window.history.replaceState(null, "", url.toString());
        }
        return;
      }
    }

    this.currentStep = 0; // Default to first step when no step parameter
  }

  private handleStepChange(e: CustomEvent<{ step: number }>) {
    this.currentStep = e.detail.step;
  }

  private handleSignupSuccess(e: CustomEvent<{ email: string }>) {
    this.userEmail = e.detail.email;
    const wizard = this.renderRoot.querySelector("wow-wizard");
    wizard?.next();
  }

  // Onboarding form methods
  private async handleHandleInput(e: Event) {
    const handleInput = e.target as HTMLInputElement;
    const handleValue = handleInput.value;

    const errorMessage = await this.validateHandle(handleValue);
    handleInput.setCustomValidity(errorMessage);
  }

  private async validateHandle(value: string) {
    this.handleError = "";

    const currentUser = this.authState.currentUser.get();
    if (currentUser?.handle && currentUser?.handle !== "") {
      // If the user already has a handle, it's considered valid
      this.isHandleAvailable = true;
      this.handleError = "";
      return "";
    }

    if (!value) {
      const errorMessage = translate("onboarding.handleValidationLength");
      this.handleError = errorMessage;
      return errorMessage;
    }

    if (value.length < 3) {
      const errorMessage = translate("onboarding.handleValidationLength");
      this.handleError = errorMessage;
      return errorMessage;
    }

    if (!/^[a-z0-9_\-.]+$/.test(value)) {
      const errorMessage = translate("onboarding.handleValidationPattern");
      this.handleError = errorMessage;
      return errorMessage;
    }

    try {
      const { available } = await rpc("checkHandle", { handle: value.toLowerCase() });
      this.isHandleAvailable = available;

      if (!available) {
        const errorMessage = translate("onboarding.handleUnavailable");
        this.handleError = errorMessage;
        return errorMessage;
      }

      this.handleError = "";
      return "";
    } catch (error) {
      console.error("Handle validation error:", error);
      this.isHandleAvailable = false;
      const validationError = translate("onboarding.handleUnavailable");
      this.handleError = validationError;
      return validationError;
    }
  }

  private handleAddMoreClicked() {
    this.isAddSocialModalOpen = true;
  }

  private handleModalClose() {
    this.isAddSocialModalOpen = false;
  }

  private handleSocialLinksUpdated(event: CustomEvent) {
    const { socialLinks } = event.detail;
    this.currentSocialLinks = socialLinks;
    this.hasSocialLinks = socialLinks.length > 0;
  }

  private handleSocialLinksChanged(event: CustomEvent) {
    const { socialLinks } = event.detail;
    this.currentSocialLinks = socialLinks;
    this.hasSocialLinks = socialLinks.length > 0;

    // Clear the error when social links change
    if (this.hasSocialLinks && this.socialLinksError) {
      this.socialLinksError = "";
    }
  }

  private handleAutoFocusUsed() {
    this.newlyAddedLinkIndex = -1;
  }

  // Step-2 methods (previously step 3)
  private isStep2Valid(): boolean {
    return !!(
      (this.profileImageFileId || this.profileDataQuery.result.data?.profileImageUrl) &&
      (this.profileCoverFileId || this.profileDataQuery.result.data?.backgroundImageUrl) &&
      (this.selectedThemeId ||
        this.customColors ||
        this.profileDataQuery.result.data?.customColors ||
        this.profileDataQuery.result.data?.theme)
    );
  }

  private getCurrentThemeColors(): { primary: string; secondary: string } | undefined {
    // Priority 1: If custom colors are selected, use them
    if (this.customColors) {
      return {
        primary: this.customColors.primary,
        secondary: this.customColors.secondary,
      };
    }
    // Priority 2: If a theme is selected, use its colors
    if (this.selectedThemeId && this.selectedThemeColors) {
      return {
        primary: this.selectedThemeColors.primary,
        secondary: this.selectedThemeColors.secondary,
      };
    }
    // No dynamic state available
    return undefined;
  }

  private handleCoverImageUploaded = (event: CustomEvent) => {
    const { fileId } = event.detail;
    this.profileCoverFileId = fileId;
  };

  private handleAvatarUploaded = (event: CustomEvent) => {
    const { fileId } = event.detail;
    this.profileImageFileId = fileId;
  };

  private handleColorSelect(e: CustomEvent) {
    const { id, colorPair } = e.detail;
    this.selectedThemeId = id;
    this.selectedThemeColors = colorPair ? { primary: colorPair.primary, secondary: colorPair.secondary } : undefined;
    // Clear custom colors when a theme is selected
    this.customColors = undefined;
  }

  private handleCustomColorSelect() {
    this.isCustomColorModalOpen = true;
    // Clear selected theme when going to custom
    this.selectedThemeId = undefined;
  }

  private handleCustomColorSave(event: CustomEvent) {
    const { colors } = event.detail;
    this.customColors = colors;
    this.isCustomColorModalOpen = false;
  }

  private handleCustomColorCancel() {
    this.isCustomColorModalOpen = false;
  }

  private editSocialDetail: { platformName: string; index: number } | undefined = undefined;

  private handleEditPlatformClicked(event: CustomEvent) {
    const { platform, index } = event.detail;

    this.editSocialDetail = { platformName: platform, index };
    this.isAddSocialModalOpen = true;
  }

  private handleAddLinkClicked(event: CustomEvent) {
    const { platform } = event.detail;
    const platformConfig = platformConfigs.find((p) => p.platform === platform);
    if (!platformConfig) return;

    let newLinkUrl = "";

    if (this.editSocialDetail) {
      const { index, platformName } = this.editSocialDetail;

      const oldPlatformConfig = platformConfigs.find((p) => p.platformName === platformName);
      const link = this.currentSocialLinks[index] ?? "";

      if (!oldPlatformConfig || !link) return;

      // Extract username from existing URL using regex
      const oldDomain = oldPlatformConfig.domains[0] ?? "";
      const oldPrefix = getUsernamePrefix(oldPlatformConfig.platform);

      // Match pattern like "https://domain/...prefix...username"
      const usernameRegex = new RegExp(`${oldDomain}/(?:${oldPrefix})?([^/?#]+)`, "i");
      const match = link.match(usernameRegex);
      const username = match?.[1] ?? "";

      // Build new platform link
      const newDomain = platformConfig.domains[0] ?? "";
      const newPrefix = getUsernamePrefix(platformConfig.platform);

      // Rebuild full URL using extracted username
      if (platformConfig.platform === "email") {
        newLinkUrl = username; // For email, username is full address
      } else {
        newLinkUrl = `https://${newDomain}/${newPrefix}${username}`;
      }

      // Replace link in array
      this.currentSocialLinks = this.currentSocialLinks.map((l, i) => (i === index ? newLinkUrl : l));

      // reset edit link detail
      this.editSocialDetail = undefined;

      this.newlyAddedLinkIndex = index;
    } else {
      if (platformConfig.platform === "email") {
        this.currentSocialLinks = [...this.currentSocialLinks, newLinkUrl];
      } else {
        // Create new link URL and add to current social links
        const usernamePrefix = getUsernamePrefix(platformConfig.platform);

        newLinkUrl = `https://${platformConfig.domains[0]}/${usernamePrefix}`;
        this.currentSocialLinks = [...this.currentSocialLinks, newLinkUrl];
      }

      this.newlyAddedLinkIndex = this.currentSocialLinks.length - 1;
    }

    this.dispatchEvent(
      new CustomEvent("social-links-updated", {
        detail: { socialLinks: this.currentSocialLinks },
        bubbles: true,
      }),
    );

    this.isAddSocialModalOpen = false;
  }

  private renderSocialLinksInModal() {
    return html` <div class="social-links-content">${this.renderPlatformAddButtons(platformConfigs)}</div> `;
  }

  private renderPlatformAddButtons(platforms: (typeof platformConfigs)[number][]) {
    return platforms.map((platform) => {
      return html`
        <social-media-item
          .icon=${platform.colorfulIcon}
          .platformName=${platform.platformName}
          .platform=${platform.platform}
          .mode=${"add-button"}
          .addText=${t("profile.addLink")}
          .addAriaLabel=${t("profile.addLinkAriaLabel", { platformName: platform.platformName })}
          @add-link-clicked=${this.handleAddLinkClicked}
        ></social-media-item>
      `;
    });
  }

  private renderStep1Content() {
    if (this.currentStep !== 1) return nothing;

    const submitDisabled = this.hasSocialLinks == false || !!this.handleError || this.isHandleAvailable == false;

    return html`
      <div class="content">
        <div class="onboarding-scroll-container">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>

          <div class="onboarding-main-content">
            <div class="onboarding-header">
              <div class="onboarding-title-section">
                <h1 class="onboarding-title">${t("onboarding.pickHandle")}</h1>
                <p class="onboarding-subtitle">${t("onboarding.handleDescription")}</p>
              </div>
            </div>

            <div class="onboarding-form-section">
              <wow-input
                form="onboard-form"
                type="text"
                name=${this.onboardFormController.fieldNames.handle}
                placeholder=${t("onboarding.handlePlaceholder")}
                required
                info-message=${this.isHandleAvailable ? t("onboarding.handleAvailable") : ""}
              ></wow-input>

              <social-links-section
                .socialLinks=${this.currentSocialLinks}
                .editable=${true}
                .autoFocusIndex=${this.newlyAddedLinkIndex}
                @add-more-clicked=${this.handleAddMoreClicked}
                @social-link-clicked=${this.handleSocialLinkClicked}
                @social-links-changed=${this.handleSocialLinksChanged}
                @auto-focus-used=${this.handleAutoFocusUsed}
                @edit-platform-clicked=${this.handleEditPlatformClicked}
              ></social-links-section>
            </div>
          </div>
        </div>
      </div>
      <wow-sticky-button-container desktop-static>
        <wow-button class="onboarding-submit-button" form="onboard-form" type="submit" ?disabled=${submitDisabled}>
          ${t("onboarding.continue")}
        </wow-button>
      </wow-sticky-button-container>
    `;
  }

  private renderStep2Content() {
    if (this.currentStep !== 2) return nothing;

    return html`
      <div class="content">
        <div class="onboarding-scroll-container">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>

          <div class="onboarding-main-content">
            <div class="onboarding-header">
              <div class="onboarding-title-section">
                <h1 class="onboarding-title">${t("onboarding.chooseStyle")}</h1>
                <p class="onboarding-subtitle">${t("onboarding.styleDescription")}</p>
              </div>
            </div>

            <div class="onboarding-form-section">
              <user-background-image
                .primaryColor=${this.getCurrentThemeColors()?.primary}
                .secondaryColor=${this.getCurrentThemeColors()?.secondary}
                .socialLinks=${this.currentSocialLinks}
                userHandle=${this.onboardFormController.getValue("handle")}
                .onboarding=${true}
                @cover-image-uploaded=${this.handleCoverImageUploaded}
              >
                <wow-avatar-editor @avatar-uploaded=${this.handleAvatarUploaded}></wow-avatar-editor>
              </user-background-image>

              <profile-color-selector
                .selectedId=${this.selectedThemeId}
                .primaryColor=${this.customColors?.primary}
                .secondaryColor=${this.customColors?.secondary}
                @color-selected=${this.handleColorSelect}
                @custom-color-selected=${this.handleCustomColorSelect}
              ></profile-color-selector>
            </div>
          </div>
        </div>
      </div>
      <wow-sticky-button-container desktop-static>
        <wow-button
          class="onboarding-submit-button"
          form="onboard-form"
          type="submit"
          ?disabled=${!this.isStep2Valid()}
        >
          ${t("onboarding.createProfile")}
        </wow-button>
      </wow-sticky-button-container>
    `;
  }

  private handleSocialLinkClicked() {
    // Handle social link click action
    // TODO: Implement social link click handling
  }

  protected override render() {
    return html`
      <div>
        <form id="onboard-form"></form>
        <wow-wizard .currentStep=${this.currentStep} .totalSteps=${3} @step-change=${this.handleStepChange}>
          <div class="scrollable-step" aria-label="step-0">
            <wow-sign-up-create @signup-success=${this.handleSignupSuccess}></wow-sign-up-create>
          </div>
          <div class="container" aria-label="step-1">${this.renderStep1Content()}</div>
          <div class="container" aria-label="step-2">${this.renderStep2Content()}</div>
        </wow-wizard>

        ${this.isAddSocialModalOpen
          ? html`
              <wow-modal @close=${this.handleModalClose}>
                <span slot="header">${t("profile.addSocialPlatform")}</span>
                ${this.renderSocialLinksInModal()}
              </wow-modal>
            `
          : ""}
        ${this.isCustomColorModalOpen
          ? html`
              <custom-color-picker
                .initialColors=${this.customColors}
                @save=${this.handleCustomColorSave}
                @cancel=${this.handleCustomColorCancel}
              ></custom-color-picker>
            `
          : ""}
      </div>
    `;
  }
}
